/* eslint-disable @next/next/no-html-link-for-pages */
import { zodResolver } from "@hookform/resolvers/zod";
import { GetServerSideProps } from "next";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { CountryNavbarComponent } from "../components/Auth/CountryDropdown/CountryDropdown";
import {
  ResetPasswordFormSchema,
  ResetPasswordFormSchemaType,
} from "../components/Auth/ResetPassword/ResetFormSchema";
import { ResetPasswordComponent } from "../components/Auth/ResetPassword/ResetPassword";
import AuthLayout from "../components/Layouts/Auth/Auth";
import { resetPassword } from "../lib/controllers/auth/resetPassword";
import useSessionStorage from "../lib/hooks/useSessionStorage";

import { signIn } from "next-auth/react";
import { CreatePasswordComponent } from "../components/Auth/CreatePassword/CreatePassword";
import {
  CreatePasswordFormSchema,
  CreatePasswordFormSchemaType,
  CreatePasswordPayload,
} from "../components/Auth/CreatePassword/CreatePasswordFormSchema";
import { AuthOtpComponent } from "../components/Auth/Otp/Otp";
import {
  OtpFormSchema,
  OtpFormSchemaType,
} from "../components/Auth/Otp/OtpFormSchema";
import { createPassword } from "../lib/controllers/auth/createPassword";
import { verifyResetPasswordToken } from "../lib/controllers/auth/verifyResetToken";
import { getIpCountry } from "../lib/helper/getIpCountry";
import tenantConfig from "../tenant.config";
import { PrimeLink } from "../components/prime/link";
import { featureFlag } from "../lib/featureFlags/featureFlags";

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const country = getIpCountry(req);
  //If theres country code, return them as props
  return {
    props: {
      countryCode: country || null,
    },
  };
};

const IS_RECAPTCHA_ENABLED = featureFlag("recaptcha");

function ResetPasswordPage(props: { countryCode?: string }) {
  const [email, setEmail] = useState<string>("");
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [resetTokenVerified, setResetTokenVerified] = useState<boolean>(false);
  const [shouldShowOtpForm, setShouldShowOtpForm] = useState<boolean>(false);
  const useResetPasswordFormProps = useForm<ResetPasswordFormSchemaType>({
    resolver: zodResolver(ResetPasswordFormSchema),
  });
  const useOtpFormProps = useForm<OtpFormSchemaType>({
    resolver: zodResolver(OtpFormSchema),
  });
  const useCreatePasswordFormProps = useForm<CreatePasswordFormSchemaType>({
    resolver: zodResolver(CreatePasswordFormSchema),
  });
  const router = useRouter();
  const { setItem, getItem } = useSessionStorage();
  //to wait for country useEffect to load
  const [isLoading, setLoading] = useState<boolean>(true);
  const [currentCountry, setCurrentCountry] = useState<string>("");
  const [otp, setOtp] = useState<string>("");

  useEffect(() => {
    const sessionCountry = getItem("selectedCountry");
    if (sessionCountry) {
      setCurrentCountry(sessionCountry);
    } else {
      setCurrentCountry(props.countryCode === "MY" ? "MY" : "SG");
    }
    setLoading(false);
  }, [props.countryCode]);

  const setCountry = (selectedCountry: string) => {
    setCurrentCountry(selectedCountry);
    useResetPasswordFormProps.reset();
    setItem("selectedCountry", selectedCountry);
  };

  const onSubmit = async (data: any) => {
    setIsFetching(true);

    let token = "default";
    if (IS_RECAPTCHA_ENABLED) {
      // Get recaptcha token
      if (!recaptchaRef.current) throw "reCaptcha null";
      token = (await recaptchaRef.current?.executeAsync()) ?? "";
      if (!token) throw "token null";
    }

    // Request reset password OTP email
    const [response, error] = await resetPassword(data.email, currentCountry);

    // If email is Not Found, still show Email sent component
    if (error && error.message !== "Not Found") {
      useResetPasswordFormProps.setError(
        "email",
        { type: "custom", message: error.message },
        { shouldFocus: true }
      );
      setIsFetching(false);
      recaptchaRef.current?.reset();
      return;
    }

    // Save info on localStorage to make the otp expiration time persistent
    const otpCountdownData = {
      email: data.email,
      otpSentAt: new Date().toJSON(),
      action: "reset-password",
      currentCountry,
    };
    localStorage.setItem("otpCountdownData", JSON.stringify(otpCountdownData));

    setEmail(data.email);
    setShouldShowOtpForm(true);
    setIsFetching(false);
    recaptchaRef.current?.reset();
  };

  const onSubmitOtp = async (data: any) => {
    setIsFetching(true);

    // Verify OTP token
    const [tokenResponse, tokenError] = await verifyResetPasswordToken(
      email,
      data.code,
      "reset-password"
    );

    if (tokenError && tokenError.error === true) {
      useOtpFormProps.setError(
        "code",
        { type: "custom", message: tokenError.message },
        { shouldFocus: true }
      );
      setIsFetching(false);
      return;
    }

    setOtp(data.code);
    setIsFetching(false);
    setResetTokenVerified(true);
    localStorage.removeItem("otpCountdownData");
    sessionStorage.removeItem("selectedCountry");
  };

  const onSubmitPassword = async (data: CreatePasswordFormSchemaType) => {
    setIsFetching(true);

    const payload: CreatePasswordPayload = {
      email,
      password: data.password,
      otpToken: otp,
    };

    // Create new password
    const [res, err] = await createPassword(payload);
    if (err && err.error) {
      setIsFetching(false);
      useCreatePasswordFormProps.setError(
        "password",
        { type: "custom", message: err.message },
        { shouldFocus: true }
      );
      return;
    }

    //Login
    const result = await signIn("login", {
      email,
      otp: res.data.otp,
      country: currentCountry,
      redirect: false,
      callbackUrl: decodeURIComponent("/verify"),
    });

    // If login process fail, show error to user
    if (result && result.ok === false) {
      const errorMessage = result.error ?? "An error occurred"; // Use a default value if result.error is null
      useCreatePasswordFormProps.setError(
        "password",
        { type: "custom", message: errorMessage },
        { shouldFocus: true }
      );
      setIsFetching(false);
      return;
    }

    // Redirect on successful signup
    if (result && result.url) {
      sessionStorage.removeItem("selectedCountry");
      router.push(result.url!);
    }
  };

  // If diff between current time & localStorage time is < 5 mins show otp form
  useEffect(() => {
    const storedDataString = localStorage.getItem("otpCountdownData");
    if (storedDataString) {
      const storedData = JSON.parse(storedDataString);
      const email = storedData.email;
      const action = storedData.action;
      const otpSentAt = new Date(storedData.otpSentAt);
      const now = new Date();
      const timeDifference = Number(now) - Number(otpSentAt);
      const secondsDifference = Math.floor(timeDifference / 1000);

      if (action !== "reset-password") {
        localStorage.removeItem("otpCountdownData");
        return;
      }

      if (secondsDifference > 300) {
        localStorage.removeItem("otpCountdownData");
      } else {
        setEmail(email);
        setShouldShowOtpForm(true);
      }
    }
  }, []);

  const renderFooter = () => {
    switch (tenantConfig.entity_short_name.toLowerCase()) {
      case "alta":
        return (
          <>
            If you have any issues with signing in, please reach out via our
            Helpdesk by clicking on the icon below or contact us at{" "}
            <PrimeLink href={"mailto:" + tenantConfig.email.generic}>
              {tenantConfig.email.generic}
            </PrimeLink>
            .
          </>
        );
      default:
        return (
          <>
            If you have any issues with signing in, contact us at{" "}
            <PrimeLink href={"mailto:" + tenantConfig.email.generic}>
              {tenantConfig.email.generic}
            </PrimeLink>
            .
          </>
        );
    }
  };

  if (resetTokenVerified) {
    return (
      <>
        <Head>
          <title>Create password</title>
        </Head>
        <AuthLayout country={currentCountry}>
          <div>
            <h2>Create new password</h2>
            <CreatePasswordComponent
              onSubmit={onSubmitPassword}
              useFormProps={useCreatePasswordFormProps}
              isFetching={isFetching}
            />
          </div>
        </AuthLayout>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>Reset your password</title>
      </Head>
      {featureFlag("ecf") && !isLoading && (
        <CountryNavbarComponent
          countryCode={currentCountry}
          setCountry={setCountry}
        />
      )}
      <AuthLayout country={currentCountry}>
        {shouldShowOtpForm ? (
          <>
            <h1 className="font-light tracking-wide">
              Two-factor authentication
            </h1>
            <div>
              <h2>Check your email</h2>
              <p>
                <span>Please enter OTP sent to:</span>
                <br />
                <span className="font-bold text-black">{email}</span>
              </p>
              <AuthOtpComponent
                useFormProps={useOtpFormProps}
                onSubmit={onSubmitOtp}
                isFetching={isFetching}
              />
              <p className="pt-2 text-xs leading-normal">
                You will receive an email if an account exists.
                <br />
                <span className="block mt-1"></span>
                {renderFooter()}
              </p>
            </div>
          </>
        ) : (
          <>
            <div>
              <h2>Reset your password</h2>
              {IS_RECAPTCHA_ENABLED && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
                  badge="bottomleft"
                />
              )}
              <p className="text-sm">
                Enter the email that is associated with your{" "}
                {tenantConfig.entity_short_name} account.
              </p>

              <ResetPasswordComponent
                onSubmit={onSubmit}
                useFormProps={useResetPasswordFormProps}
                isFetching={isFetching}
              />
              <div className="mx-auto text-sm !mt-4 w-fit">
                Don&apos;t have an account?{" "}
                <PrimeLink href={"/signup"} nextLink>
                  Sign up
                </PrimeLink>
              </div>
            </div>
          </>
        )}
      </AuthLayout>
    </>
  );
}

export default ResetPasswordPage;
